/**
 * Framework-free gameplay preferences that persist in localStorage, so the
 * Settings screen and the sound engine share ONE source of truth (like
 * {@link ../sound/sfx}, which owns the separate sound on/off flag). No I/O, no
 * React — a couple of getters/setters over `localStorage`, safe to import from
 * anywhere (guards every storage/DOM access for private-mode + non-browser envs).
 *
 * - **volume** (0–1): a master multiplier the synthesized SFX scale their gain by,
 *   so the Settings volume slider genuinely changes how loud cues play.
 * - **liftToPlay** (the Settings «تأیید قبل از بازی کارت» row, OFF by default): a tap on a
 *   card in حکم / بی‌دل only LIFTS it and a tap on the table plays it; the tables read it
 *   when they render ({@link getLiftToPlay}).
 * - **theme** (`"auto"|"dark"|"light"`, the Settings «پوسته» row): the DEFAULT
 *   (nothing persisted yet) is **dark** for everyone; picking `auto` opts into
 *   following Telegram's colorScheme (pushed in by main.tsx via
 *   {@link setTelegramColorScheme}); the resolved theme is published as the
 *   SINGLE `data-theme` attribute on <html>, which drives BOTH the Tailwind
 *   `--c-*` channel variables and the legacy `--bg/--surface/…` variables in
 *   styles/theme.css. Listeners (the Telegram header color in
 *   telegram/viewport.ts) re-sync through {@link onThemeChange}.
 */

import { MENSCH_PLAYER_COUNTS, type MenschPlayerCount } from "@bing/engine-mensch";
import {
  DEFAULT_LADDERS_LEVEL,
  DEFAULT_LADDERS_STYLE,
  LADDERS_PLAYER_COUNTS,
  laddersLevelOf,
  laddersLiveStyleOf,
  type LaddersLevel,
  type LaddersPlayerCount,
  type LaddersStyle,
} from "@bing/engine-ladders";
import { DEFAULT_DOOZ_MATCH_LENGTH, DOOZ_MATCH_LENGTHS, type DoozMatchLength } from "@bing/shared";
import {
  DOOZ_PIECE_COLORS,
  DEFAULT_DOOZ_SEAT0_COLOR,
  type DoozPieceColor,
} from "../game/dooz-colors";
import { DEFAULT_PASUR_SEATS, pasurSeatsOrDefault } from "../game/room-sizes";
import type { PasurSeatCount } from "@bing/engine-pasur";
import {
  BACKGAMMON_MATCH_LENGTHS,
  BACKGAMMON_PUBLIC_MATCH_LENGTHS,
  DEFAULT_BACKGAMMON_MATCH_LENGTH,
  type BackgammonMatchLength,
  DEFAULT_BOT_DIFFICULTY,
  DEFAULT_CARD_DECK,
  DEFAULT_MENSCH_DIFFICULTY as SHARED_DEFAULT_MENSCH_DIFFICULTY,
  isBotDifficulty,
  isCardDeckId,
  type BotDifficulty,
  type CardDeckId,
  type GameCategory,
  HOKM_MATCH_LENGTHS,
  DEFAULT_HOKM_MATCH_LENGTH,
  DEFAULT_HOKM_MODE_KOT,
  DEFAULT_HOKM_MODES,
  type HokmMatchLength,
  DEFAULT_SHELEM_TARGET,
  shelemTargetOrDefault,
  type ShelemTarget,
} from "@bing/shared";

/** The persisted preference — `auto` defers to Telegram's colorScheme. */
export type ThemePref = "auto" | "dark" | "light";
/** What actually gets applied to <html data-theme>. */
export type ResolvedTheme = "dark" | "light";

const VOLUME_KEY = "bing:volume";
const BOT_DIFFICULTY_KEY = "bing:botDifficulty";
const MENSCH_PLAYERS_KEY = "bing:menschPlayers";
/** حکم — the OFFLINE match length. The online private table carries its own in table settings, so
 *  changing one never moves the other (the same split منچ's two player-count prefs use). */
const HOKM_MATCH_LENGTH_KEY = "bing:hokmMatchLength";
const HOKM_MODE_KOT_KEY = "bing:hokmModeKot";
/** شلم — the SOLO match target (card-67). The online private table carries its own in table
 *  settings, so changing one never moves the other (the حکم match-length split above). */
const SHELEM_TARGET_KEY = "bing:shelemTarget";
/** «تأیید قبل از بازی کارت» — lift, then tap the table. See {@link getLiftToPlay}. */
const LIFT_TO_PLAY_KEY = "bing:liftToPlay";
/** حکم — are the three suit-less calls on offer in a SOLO game? See {@link getHokmModes}. */
const HOKM_MODES_KEY = "bing:hokmModes";
/** حکم — which PUBLIC room to quick-match into. See {@link getHokmPublicAdvanced}. */
const HOKM_PUBLIC_ADVANCED_KEY = "bing:hokmPublicAdvanced";
/** تخته نرد — which PUBLIC room to quick-match into. See {@link getBackgammonPublicMatchTo}. */
const BG_PUBLIC_MATCH_KEY = "bing:bgPublicMatchTo";
/** پاسور — which PUBLIC room (size) to quick-match into. See {@link getPasurPublicSeats}. */
const PASUR_PUBLIC_SEATS_KEY = "bing:pasurPublicSeats";
const MENSCH_PLAYERS_ONLINE_KEY = "bing:menschPlayersOnline";
const MENSCH_COLOR_KEY = "bing:menschColor";
const BG_COLOR_KEY = "bing:bgColor";
const CHESS_SIDE_KEY = "bing:chessSide";
const DOOZ_COLOR_KEY = "bing:doozColor";
const DOOZ_HANDS_KEY = "bing:doozHands";
const DOOZ_DIFFICULTY_KEY = "bing:doozDifficulty";

/** ITEM 12 — the two تخته نرد checker looks the board already draws. */
export type BackgammonCheckerColor = "gold" | "obsidian";
const THEME_KEY = "bing:theme";
const DECK_KEY = "bing:deck";
const LOBBY_CATEGORY_KEY = "bing:lobbyCategory";

/** Default master volume (80%) when the user hasn't chosen one yet. */
const DEFAULT_VOLUME = 0.8;

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_VOLUME;
  return Math.min(1, Math.max(0, value));
}

function readVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    return raw == null ? DEFAULT_VOLUME : clampVolume(Number(raw));
  } catch {
    return DEFAULT_VOLUME;
  }
}

function readBotDifficulty(): BotDifficulty {
  try {
    const raw = localStorage.getItem(BOT_DIFFICULTY_KEY);
    return isBotDifficulty(raw) ? raw : DEFAULT_BOT_DIFFICULTY;
  } catch {
    return DEFAULT_BOT_DIFFICULTY;
  }
}

function readCardDeck(): CardDeckId {
  try {
    const raw = localStorage.getItem(DECK_KEY);
    return isCardDeckId(raw) ? raw : DEFAULT_CARD_DECK;
  } catch {
    return DEFAULT_CARD_DECK;
  }
}

function readThemePref(): ThemePref {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    // Default (nothing stored / garbage) is DARK for everyone; `auto` (follow
    // Telegram) is an explicit opt-in from the Settings «پوسته» row.
    return raw === "dark" || raw === "light" || raw === "auto" ? raw : "dark";
  } catch {
    return "dark";
  }
}

let volume = readVolume();
/**
 * منچ table size, remembered SEPARATELY for solo and online because their sensible
 * defaults differ: offline you are playing the bots and a head-to-head ۲ نفره is the
 * natural game, while an online table is a full four-handed board.
 */
function readMenschPlayers(key: string, fallback: MenschPlayerCount): MenschPlayerCount {
  try {
    const raw = Number(localStorage.getItem(key));
    return (MENSCH_PLAYER_COUNTS as readonly number[]).includes(raw)
      ? (raw as MenschPlayerCount)
      : fallback;
  } catch {
    return fallback;
  }
}

let botDifficulty = readBotDifficulty();
let menschPlayers = readMenschPlayers(MENSCH_PLAYERS_KEY, 2);
let menschPlayersOnline = readMenschPlayers(MENSCH_PLAYERS_ONLINE_KEY, 4);

/** ITEM 8 — the arm-colour the player picked for themselves (0..3). Default gold. */
function readMenschColor(): number {
  try {
    const raw = Number(localStorage.getItem(MENSCH_COLOR_KEY));
    return Number.isInteger(raw) && raw >= 0 && raw < 4 ? raw : 0;
  } catch {
    return 0;
  }
}
let menschColor = readMenschColor();

/** ITEM 12 — which checker colour the player takes in تخته نرد. Default gold (as it always was). */
function readBackgammonColor(): BackgammonCheckerColor {
  try {
    return localStorage.getItem(BG_COLOR_KEY) === "obsidian" ? "obsidian" : "gold";
  } catch {
    return "gold";
  }
}
let backgammonColor = readBackgammonColor();

/** ITEM 7 — the OFFLINE chess side the player takes: 0 = White (moves first), 1 = Black. */
function readChessSide(): 0 | 1 {
  try {
    return localStorage.getItem(CHESS_SIDE_KEY) === "1" ? 1 : 0;
  } catch {
    return 0;
  }
}
let chessSide: 0 | 1 = readChessSide();

/* ---- دوز ---------------------------------------------------------------------------------- *
 * Three remembered choices, all of them the player's own: the colour their OWN pieces render in
 * (viewer-side — the opponent always takes the contrasting one, so two players who both pick
 * قرمز still agree on the position and only the palette differs per screen), how many دست a solo
 * match runs, and how hard the bot plays.
 * -------------------------------------------------------------------------------------------- */
function readDoozColor(): DoozPieceColor {
  try {
    const raw = localStorage.getItem(DOOZ_COLOR_KEY);
    return (DOOZ_PIECE_COLORS as readonly string[]).includes(raw ?? "")
      ? (raw as DoozPieceColor)
      : DEFAULT_DOOZ_SEAT0_COLOR;
  } catch {
    return DEFAULT_DOOZ_SEAT0_COLOR;
  }
}
let doozColor = readDoozColor();

function readDoozHands(): DoozMatchLength {
  try {
    const raw = Number(localStorage.getItem(DOOZ_HANDS_KEY));
    return (DOOZ_MATCH_LENGTHS as readonly number[]).includes(raw)
      ? (raw as DoozMatchLength)
      : DEFAULT_DOOZ_MATCH_LENGTH;
  } catch {
    return DEFAULT_DOOZ_MATCH_LENGTH;
  }
}
let doozHands = readDoozHands();

function readDoozDifficulty(): BotDifficulty {
  try {
    const raw = localStorage.getItem(DOOZ_DIFFICULTY_KEY);
    return isBotDifficulty(raw) ? raw : DEFAULT_BOT_DIFFICULTY;
  } catch {
    return DEFAULT_BOT_DIFFICULTY;
  }
}
let doozDifficulty = readDoozDifficulty();

let cardDeck = readCardDeck();
let themePref = readThemePref();
/** Telegram's scheme (the `auto` target) — dark until main.tsx reports it. */
let telegramScheme: ResolvedTheme = "dark";
const themeListeners = new Set<(theme: ResolvedTheme) => void>();

export function getVolume(): number {
  return volume;
}

export function setVolume(value: number): void {
  volume = clampVolume(value);
  try {
    localStorage.setItem(VOLUME_KEY, String(volume));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * Preferred AI opponent strength for SOLO games (and a host's bot-fill on private
 * online tables). Persisted per-user so it's remembered across sessions; the game
 * connectors read it and pass `difficulty` in the room's create/join options.
 * Defaults to `medium` (متوسط).
 */
export function getBotDifficulty(): BotDifficulty {
  return botDifficulty;
}

export function setBotDifficulty(value: BotDifficulty): void {
  botDifficulty = value;
  try {
    localStorage.setItem(BOT_DIFFICULTY_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

const CHESS_DIFFICULTY_KEY = "bing:chessDifficulty";
function readChessDifficulty(): BotDifficulty {
  try {
    const raw = localStorage.getItem(CHESS_DIFFICULTY_KEY);
    return isBotDifficulty(raw) ? raw : DEFAULT_BOT_DIFFICULTY;
  } catch {
    return DEFAULT_BOT_DIFFICULTY;
  }
}
let chessDifficulty = readChessDifficulty();

/**
 * Chess has its OWN opponent-difficulty pref (`bing:chessDifficulty`), INDEPENDENT of
 * the shared solo setting (`bing:botDifficulty`) that governs the card games — it's set
 * right on the chess table. Defaults to `medium` (متوسط). Read at (re)start of a solo game.
 */
export function getChessDifficulty(): BotDifficulty {
  return chessDifficulty;
}

export function setChessDifficulty(value: BotDifficulty): void {
  chessDifficulty = value;
  try {
    localStorage.setItem(CHESS_DIFFICULTY_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

function readLobbyCategory(): GameCategory {
  try {
    const raw = localStorage.getItem(LOBBY_CATEGORY_KEY);
    return raw === "board" ? "board" : "card";
  } catch {
    return "card";
  }
}
let lobbyCategory = readLobbyCategory();

/**
 * The lobby's selected category tab («بازی‌های کارتی» / «بازی‌های تخته‌ای»). Persisted so that
 * leaving the lobby (opening a game, switching to another bottom-nav tab) and returning keeps the
 * user on the tab they chose instead of snapping back to the card default. Defaults to `card`.
 */
export function getLobbyCategory(): GameCategory {
  return lobbyCategory;
}

export function setLobbyCategory(value: GameCategory): void {
  lobbyCategory = value;
  try {
    localStorage.setItem(LOBBY_CATEGORY_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

const MENSCH_DIFFICULTY_KEY = "bing:mensch-difficulty";
/**
 * منچ DEFAULTS TO «حریف حرفه‌ای». Its strong level costs 1.83 µs a decision against the normal
 * one's 0.28 µs (measured), so there is no reason for the weaker bot to be what a player meets
 * by default. `medium` on the wire is «حریف معمولی»; `hard` is «حریف حرفه‌ای».
 *
 * ⚠️ DECLARED BEFORE THE READER, and that ordering is load-bearing: the reader runs at MODULE
 * INIT (`let menschDifficulty = readMenschDifficulty()`), so a `const` below it would be in its
 * temporal dead zone — a runtime throw that a typecheck does not catch.
 */
const DEFAULT_MENSCH_DIFFICULTY: BotDifficulty = SHARED_DEFAULT_MENSCH_DIFFICULTY;

/** منچ offers exactly TWO levels; anything else on the way in becomes the default. */
function readMenschDifficulty(): BotDifficulty {
  try {
    const raw = localStorage.getItem(MENSCH_DIFFICULTY_KEY);
    if (raw === "hard") return "hard";
    if (raw === "medium") return "medium";
    return DEFAULT_MENSCH_DIFFICULTY; // absent, or a stale third value from before منچ had two
  } catch {
    return DEFAULT_MENSCH_DIFFICULTY;
  }
}
let menschDifficulty = readMenschDifficulty();

/**
 * منچ (منچ) has its OWN opponent-difficulty pref (`bing:mensch-difficulty`), INDEPENDENT of the
 * shared card-games setting — it is chosen right on the منچ start screen, like chess's and
 * backgammon's.
 *
 * ⚠️ TWO VALUES ONLY (`medium` = «حریف معمولی», `hard` = «حریف حرفه‌ای»). منچ cannot support three
 * distinguishable opponents — 80.3% of its turns offer no choice at all — so a third label would
 * be a promise the game cannot keep. See `MENSCH_BOT_LEVELS` in `@bing/shared`.
 */
export function getMenschDifficulty(): BotDifficulty {
  return menschDifficulty;
}

export function setMenschDifficulty(value: BotDifficulty): void {
  // ⚠️ BOTH منچ levels must be settable, and only the THIRD one may be rejected. Written as
  // `value === "hard" ? "hard" : DEFAULT` this silently pinned everyone to «حریف حرفه‌ای» the
  // moment that became the default — «حریف معمولی» could not be chosen at all. Caught by a test;
  // keep the accept-list explicit.
  menschDifficulty = value === "hard" || value === "medium" ? value : DEFAULT_MENSCH_DIFFICULTY;
  try {
    localStorage.setItem(MENSCH_DIFFICULTY_KEY, menschDifficulty);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

const BG_DIFFICULTY_KEY = "bing:bg-difficulty";
function readBgDifficulty(): BotDifficulty {
  try {
    const raw = localStorage.getItem(BG_DIFFICULTY_KEY);
    return isBotDifficulty(raw) ? raw : DEFAULT_BOT_DIFFICULTY;
  } catch {
    return DEFAULT_BOT_DIFFICULTY;
  }
}
let bgDifficulty = readBgDifficulty();

/**
 * Backgammon (تخته نرد) has its OWN opponent-difficulty pref (`bing:bg-difficulty`),
 * INDEPENDENT of both the shared solo setting (`bing:botDifficulty`) and chess — it's set
 * right on the backgammon table. Defaults to `medium` (متوسط). Read at (re)start of a solo game.
 */
export function getBackgammonDifficulty(): BotDifficulty {
  return bgDifficulty;
}

export function setBackgammonDifficulty(value: BotDifficulty): void {
  bgDifficulty = value;
  try {
    localStorage.setItem(BG_DIFFICULTY_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * The card-deck ("skin") the player sees in SOLO/offline games — persisted per-user
 * (an online table uses the OWNER's `settings.deck` instead). Read when a solo game
 * mounts (via App's CardDeckContext provider); a change applies to the next game.
 * Defaults to `standard` (the current illustrated back).
 */
export function getCardDeck(): CardDeckId {
  return cardDeck;
}

export function setCardDeck(value: CardDeckId): void {
  cardDeck = value;
  try {
    localStorage.setItem(DECK_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

export function getThemePref(): ThemePref {
  return themePref;
}

/** The theme that should be (and, after {@link applyThemePref}, is) on <html>. */
export function getResolvedTheme(): ResolvedTheme {
  return themePref === "auto" ? telegramScheme : themePref;
}

/** Persist the «پوسته» choice and apply it live. */
export function setThemePref(value: ThemePref): void {
  themePref = value;
  try {
    localStorage.setItem(THEME_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
  applyThemePref();
}

/** Called once at launch (main.tsx) with Telegram's colorScheme — the target of
 * the default `auto` preference. Re-applies so `auto` users follow Telegram. */
export function setTelegramColorScheme(scheme: ResolvedTheme): void {
  telegramScheme = scheme;
  applyThemePref();
}

/** Subscribe to applied-theme changes (e.g. the Telegram header color sync).
 * Returns an unsubscribe. Listeners also fire on re-applies with no change —
 * they must be idempotent. */
export function onThemeChange(listener: (theme: ResolvedTheme) => void): () => void {
  themeListeners.add(listener);
  return () => themeListeners.delete(listener);
}

/** Stamp the resolved theme as `data-theme` on <html> (the ONE attribute both
 * the Tailwind `--c-*` vars and the legacy vars key on) + notify listeners. */
export function applyThemePref(): void {
  const resolved = getResolvedTheme();
  if (typeof document !== "undefined") {
    document.documentElement.dataset["theme"] = resolved;
  }
  for (const listener of themeListeners) {
    try {
      listener(resolved);
    } catch {
      // a bad listener must never break theme application
    }
  }
}

// Publish the persisted theme once on load so the first paint is right.
applyThemePref();

/** SOLO منچ table size — defaults to ۲ نفره (you against one bot). */
export function getMenschPlayers(): MenschPlayerCount {
  return menschPlayers;
}

export function setMenschPlayers(value: MenschPlayerCount): void {
  menschPlayers = value;
  try {
    localStorage.setItem(MENSCH_PLAYERS_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/** ONLINE منچ table size — defaults to ۴ نفره (a full board). */
export function getMenschPlayersOnline(): MenschPlayerCount {
  return menschPlayersOnline;
}

export function setMenschPlayersOnline(value: MenschPlayerCount): void {
  menschPlayersOnline = value;
  try {
    localStorage.setItem(MENSCH_PLAYERS_ONLINE_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/* ---- مار پله (card-86) -------------------------------------------------------------------
 * A table for TWO or for FOUR on every door, four when nobody chose (the planner's call 10). Two
 * prefs, split as منچ's two seat-count prefs are, so moving one never moves the other:
 *   · the SOLO size, remembered per device (the start screen's picker);
 *   · which PUBLIC room to quick-match into — a COURIER like پاسور's: the lobby writes it on BOTH
 *     taps and `LaddersOnlineGame` reads it a moment later to name the room (a size is a room type,
 *     so it is a QUEUE). A private host's size is neither: it rides that table's create intent.
 * Both VALIDATED on read and on write, so a stale or crafted entry lands at the table for four —
 * never at a room the server does not define. */
const LADDERS_PLAYERS_KEY = "bing:ladders-players";
const LADDERS_PUBLIC_SEATS_KEY = "bing:ladders-public-seats";
/** The size a مار پله table is when nobody chose one. */
export const DEFAULT_LADDERS_SEATS: LaddersPlayerCount = 4;

/** A size the platform offers (۲ or ۴), else {@link DEFAULT_LADDERS_SEATS}. */
export function laddersSeatsOrDefault(raw: unknown): LaddersPlayerCount {
  return (LADDERS_PLAYER_COUNTS as readonly unknown[]).includes(raw)
    ? (raw as LaddersPlayerCount)
    : DEFAULT_LADDERS_SEATS;
}

function readLaddersSeats(key: string): LaddersPlayerCount {
  try {
    return laddersSeatsOrDefault(Number(localStorage.getItem(key)));
  } catch {
    return DEFAULT_LADDERS_SEATS;
  }
}

function writeLaddersSeats(key: string, value: LaddersPlayerCount): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

let laddersPlayers = readLaddersSeats(LADDERS_PLAYERS_KEY);
let laddersPublicSeats = readLaddersSeats(LADDERS_PUBLIC_SEATS_KEY);

/** SOLO مار پله table size — ۴ نفره until the player picks ۲. */
export function getLaddersPlayers(): LaddersPlayerCount {
  return laddersPlayers;
}

export function setLaddersPlayers(value: number): void {
  laddersPlayers = laddersSeatsOrDefault(value);
  writeLaddersSeats(LADDERS_PLAYERS_KEY, laddersPlayers);
}

/** Which PUBLIC مار پله room to quick-match into: the table for four or the table for two. */
export function getLaddersPublicSeats(): LaddersPlayerCount {
  return laddersPublicSeats;
}

export function setLaddersPublicSeats(value: number): void {
  laddersPublicSeats = laddersSeatsOrDefault(value);
  writeLaddersSeats(LADDERS_PUBLIC_SEATS_KEY, laddersPublicSeats);
}

/* ROUND 2 — the SOLO level and look, remembered per device (the start screen's two pickers, as
 * the solo size is). VALIDATED on read and on write by the engine's own readers: a stored level the
 * engine does not know reads as متوسط, and a look this build cannot draw — a crafted id, off
 * `LADDERS_LIVE_STYLES` — as «جنگل». A private host's level and look are
 * neither: «تنظیمات میز» opens on the defaults and they ride that table's settings. */
const LADDERS_LEVEL_KEY = "bing:ladders-level";
const LADDERS_STYLE_KEY = "bing:ladders-style";

function readLaddersLevel(): LaddersLevel {
  try {
    return laddersLevelOf(localStorage.getItem(LADDERS_LEVEL_KEY)) ?? DEFAULT_LADDERS_LEVEL;
  } catch {
    return DEFAULT_LADDERS_LEVEL;
  }
}

function readLaddersStyle(): LaddersStyle {
  try {
    return laddersLiveStyleOf(localStorage.getItem(LADDERS_STYLE_KEY)) ?? DEFAULT_LADDERS_STYLE;
  } catch {
    return DEFAULT_LADDERS_STYLE;
  }
}

let laddersLevel = readLaddersLevel();
let laddersStyle = readLaddersStyle();

/** SOLO مار پله level — متوسط until the player picks another. */
export function getLaddersLevel(): LaddersLevel {
  return laddersLevel;
}

export function setLaddersLevel(value: string): void {
  laddersLevel = laddersLevelOf(value) ?? DEFAULT_LADDERS_LEVEL;
  try {
    localStorage.setItem(LADDERS_LEVEL_KEY, laddersLevel);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/** SOLO مار پله look — «جنگل» until the player picks another this build can draw. */
export function getLaddersStyle(): LaddersStyle {
  return laddersStyle;
}

export function setLaddersStyle(value: string): void {
  laddersStyle = laddersLiveStyleOf(value) ?? DEFAULT_LADDERS_STYLE;
  try {
    localStorage.setItem(LADDERS_STYLE_KEY, laddersStyle);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * ITEM 12 — the تخته نرد checker colour the player takes.
 *
 * Purely a VIEWER-side choice, like a card back: the board always renders «my» checkers one way
 * and the opponent's the other, so each player can pick their own without the two disagreeing
 * about the position. Nothing in the engine or on the wire changes.
 */
const BG_MATCH_KEY = "bing:bgMatchLength";
let backgammonMatchLength: BackgammonMatchLength = (() => {
  try {
    const raw = Number(localStorage.getItem(BG_MATCH_KEY));
    return (BACKGAMMON_MATCH_LENGTHS as readonly number[]).includes(raw)
      ? (raw as BackgammonMatchLength)
      : DEFAULT_BACKGAMMON_MATCH_LENGTH;
  } catch {
    return DEFAULT_BACKGAMMON_MATCH_LENGTH;
  }
})();

/**
 * تخته نرد — WHICH PUBLIC ROOM to quick-match into: the conventional five points, or the short
 * three. Not a preference so much as a COURIER — the lobby writes it on the tap and
 * `BackgammonOnlineGame` reads it a moment later to build the room name.
 *
 * It has to be a QUEUE rather than a table setting: a room's name and `maxClients` are fixed at
 * `define()` time, and a quick-match table has nobody to agree a length for the stranger who joins
 * it. The server FORCES the length from whichever room you land in; this only chooses which.
 *
 * ⚠️ SEPARATE FROM {@link getBackgammonMatchLength}, the SOLO/private picker (1/3/5/7). Moving one
 * must never move the other — the same split منچ's two seat-count prefs use.
 *
 * VALIDATED ON BOTH SIDES against {@link BACKGAMMON_PUBLIC_MATCH_LENGTHS}, so a stale entry (a `7`
 * left by the private picker, a crafted value, a `3` from a build where the split was off) can
 * only ever land somebody at the conventional table — never at a room the server does not define.
 */
let backgammonPublicMatchTo: number = (() => {
  try {
    return publicMatchToOrDefault(Number(localStorage.getItem(BG_PUBLIC_MATCH_KEY)));
  } catch {
    return DEFAULT_BACKGAMMON_MATCH_LENGTH;
  }
})();

function publicMatchToOrDefault(raw: number): number {
  return (BACKGAMMON_PUBLIC_MATCH_LENGTHS as readonly number[]).includes(raw)
    ? raw
    : DEFAULT_BACKGAMMON_MATCH_LENGTH;
}

export function getBackgammonPublicMatchTo(): number {
  return backgammonPublicMatchTo;
}

export function setBackgammonPublicMatchTo(value: number): void {
  backgammonPublicMatchTo = publicMatchToOrDefault(value);
  try {
    localStorage.setItem(BG_PUBLIC_MATCH_KEY, String(backgammonPublicMatchTo));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * پاسور — WHICH PUBLIC ROOM to quick-match into: the four-seat partnership table or the table for two
 * (operator, 2026-10-20). A COURIER, exactly like {@link getBackgammonPublicMatchTo}: the lobby writes
 * it on BOTH taps and `PasurOnlineGame` reads it a moment later to name the room — a size is a room
 * type, so it is a QUEUE, never a table setting.
 *
 * ⚠️ NOT THE PRIVATE SIZE. A host's ۲ or ۴ on «تنظیمات میز» rides that table's create intent and is
 * never remembered (the operator's «default 4»); moving one must never move the other.
 *
 * VALIDATED ON BOTH SIDES against the engine's sizes, so a stale or crafted entry can only ever land
 * somebody at the four-seat table — never at a room the server does not define.
 */
let pasurPublicSeats: PasurSeatCount = (() => {
  try {
    return pasurSeatsOrDefault(Number(localStorage.getItem(PASUR_PUBLIC_SEATS_KEY)));
  } catch {
    return DEFAULT_PASUR_SEATS;
  }
})();

export function getPasurPublicSeats(): PasurSeatCount {
  return pasurPublicSeats;
}

export function setPasurPublicSeats(value: number): void {
  pasurPublicSeats = pasurSeatsOrDefault(value);
  try {
    localStorage.setItem(PASUR_PUBLIC_SEATS_KEY, String(pasurPublicSeats));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

export function getBackgammonColor(): BackgammonCheckerColor {
  return backgammonColor;
}

/**
 * ITEM 7 — which side the player takes in an OFFLINE chess game: 0 = White, 1 = Black.
 *
 * A real gameplay choice, not a skin: White moves first, and the board flips so your own pieces
 * are always at the bottom. Sent as the solo room's `side` option (the human's seat), so the
 * engine and the bot both agree about who is who.
 */
export function getChessSide(): 0 | 1 {
  return chessSide;
}

export function setChessSide(value: 0 | 1): void {
  chessSide = value;
  try {
    localStorage.setItem(CHESS_SIDE_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/** ITEM 8 — the منچ arm-colour the player chose for themselves (0..3). */
export function getMenschColor(): number {
  return menschColor;
}

export function setMenschColor(value: number): void {
  menschColor = value;
  try {
    localStorage.setItem(MENSCH_COLOR_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * The solo تخته نرد MATCH LENGTH — points, not rounds (مارس pays 2, سگ‌مارس 3 toward the target).
 * «۵» is the traditional length, so it is the default. Read at JOIN time, so a saved game always
 * finishes at the length it started with, whatever the picker says today.
 */
export function getBackgammonMatchLength(): BackgammonMatchLength {
  return backgammonMatchLength;
}

export function setBackgammonMatchLength(value: BackgammonMatchLength): void {
  backgammonMatchLength = value;
  try {
    localStorage.setItem(BG_MATCH_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

export function setBackgammonColor(value: BackgammonCheckerColor): void {
  backgammonColor = value;
  try {
    localStorage.setItem(BG_COLOR_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

// ── دوز (offline) ────────────────────────────────────────────────────────────
// The colour is VIEWER-SIDE and carries no rules meaning, so nothing about it reaches the server.
// The hand count and the difficulty are sent as room options and re-validated there.

export function getDoozColor(): DoozPieceColor {
  return doozColor;
}

export function setDoozColor(value: DoozPieceColor): void {
  doozColor = value;
  try {
    localStorage.setItem(DOOZ_COLOR_KEY, value);
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

export function getDoozHands(): DoozMatchLength {
  return doozHands;
}

export function setDoozHands(value: DoozMatchLength): void {
  doozHands = value;
  try {
    localStorage.setItem(DOOZ_HANDS_KEY, String(value));
  } catch {
    // ignore storage failures
  }
}

export function getDoozDifficulty(): BotDifficulty {
  return doozDifficulty;
}

export function setDoozDifficulty(value: BotDifficulty): void {
  doozDifficulty = value;
  try {
    localStorage.setItem(DOOZ_DIFFICULTY_KEY, value);
  } catch {
    // ignore storage failures
  }
}

// ── حکم match length (offline) ───────────────────────────────────────────────
// How many ROUNDS win the match. The server is authoritative (it clamps to 3/5/7 and ignores this
// entirely on a resume, where the persisted state already holds the target the match began with) —
// this is only what the start screen offers next time.
let hokmMatchLength: HokmMatchLength = readHokmMatchLength();

function readHokmMatchLength(): HokmMatchLength {
  try {
    const raw = Number(localStorage.getItem(HOKM_MATCH_LENGTH_KEY));
    return HOKM_MATCH_LENGTHS.includes(raw as HokmMatchLength)
      ? (raw as HokmMatchLength)
      : DEFAULT_HOKM_MATCH_LENGTH;
  } catch {
    return DEFAULT_HOKM_MATCH_LENGTH;
  }
}

export function getHokmMatchLength(): HokmMatchLength {
  return hokmMatchLength;
}

export function setHokmMatchLength(value: HokmMatchLength): void {
  hokmMatchLength = value;
  try {
    localStorage.setItem(HOKM_MATCH_LENGTH_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

// ── شلم match target (offline) ───────────────────────────────────────────────
// How many POINTS win a SOLO match: ۶۶۰ or ۱۲۰۰ — ۶۶۰ on first use (the operator, card-67), then the
// last pick, per device, as حکم's solo pickers remember theirs. The SERVER is authoritative (the solo
// room validates the pick as a private host's — `shelemTargetOrDefault` — and ignores it on a resume,
// where the persisted state holds the target the match began with), so this is only what the start
// screen offers next time. A stored value off the list (a crafted or legacy one) reads as ۶۶۰.
let shelemTarget: ShelemTarget = readShelemTarget();

function readShelemTarget(): ShelemTarget {
  try {
    // nothing stored reads `Number(null)` = 0 — off the list, so the default: one rule, no branch
    return shelemTargetOrDefault(Number(localStorage.getItem(SHELEM_TARGET_KEY)));
  } catch {
    return DEFAULT_SHELEM_TARGET;
  }
}

export function getShelemTarget(): ShelemTarget {
  return shelemTarget;
}

export function setShelemTarget(value: ShelemTarget): void {
  shelemTarget = value;
  try {
    localStorage.setItem(SHELEM_TARGET_KEY, String(value));
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

// ── حکم کوت under a suit-less call (offline) ─────────────────────────────────
// Does a 7–0 sweep pay a کوت under سرس / نرس / آس نرس? The sources treat this as a pre-game
// agreement, so the player chooses it; a خال call is unaffected either way. The SERVER is
// authoritative (it reads the option only for حکم and only as a strict boolean, and ignores it
// on a resume — the persisted state carries the setting the match began with); this is only what
// the start screen offers next time. DEFAULT OFF, the conventional reading.
let hokmModeKot: boolean = readHokmModeKot();

function readHokmModeKot(): boolean {
  try {
    // Strictly the string "1": any other stored value (including a legacy one) means OFF.
    return localStorage.getItem(HOKM_MODE_KOT_KEY) === "1";
  } catch {
    return DEFAULT_HOKM_MODE_KOT;
  }
}

export function getHokmModeKot(): boolean {
  return hokmModeKot;
}

export function setHokmModeKot(value: boolean): void {
  hokmModeKot = value;
  try {
    localStorage.setItem(HOKM_MODE_KOT_KEY, value ? "1" : "0");
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

// ── حکم suit-less CALLS (سرس / نرس / آس نرس) — offline ───────────────────────
// May the Hakem call one of the three suit-less modes at all? The sources treat the whole family
// as a pre-game agreement, so the player chooses; DEFAULT OFF, the standard game. The SERVER is
// authoritative (the solo room reads the option only for حکم, only as a strict boolean, and
// ignores it on a resume — the persisted state carries the vocabulary the match began with), so
// this is only what the start screen offers NEXT time.
//
// ⚠️ SEPARATE from {@link getHokmModeKot} and from the online table's own setting, exactly as the
// two match-length prefs are separate: changing one must never silently move another.
let hokmModes: boolean = readHokmModes();

function readHokmModes(): boolean {
  try {
    // Strictly the string "1": any other stored value (including a legacy one) means OFF.
    return localStorage.getItem(HOKM_MODES_KEY) === "1";
  } catch {
    return DEFAULT_HOKM_MODES;
  }
}

export function getHokmModes(): boolean {
  return hokmModes;
}

export function setHokmModes(value: boolean): void {
  hokmModes = value;
  try {
    localStorage.setItem(HOKM_MODES_KEY, value ? "1" : "0");
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * WHICH PUBLIC حکم ROOM — the standard «اتاق عمومی» or «اتاق عمومی پیشرفته».
 *
 * A quick-match table has no host, and the client's quick-match branch sends no settings at all
 * (the server ignores them there outright) — so «are the suit-less calls on?» cannot be a table
 * SETTING, it has to be a QUEUE. The server registers the two room types and FORCES the rule from
 * whichever one you land in; this pref is only how the lobby remembers which to matchmake into,
 * so somebody who tapped «پیشرفته» once is not thrown back to the standard room next time.
 *
 * DEFAULT FALSE — the standard game. A stored value that is not exactly "1" means the plain room,
 * so a stale or crafted entry can only ever land somebody at the conventional table.
 */
let hokmPublicAdvanced: boolean = readHokmPublicAdvanced();

function readHokmPublicAdvanced(): boolean {
  try {
    return localStorage.getItem(HOKM_PUBLIC_ADVANCED_KEY) === "1";
  } catch {
    return false;
  }
}

export function getHokmPublicAdvanced(): boolean {
  return hokmPublicAdvanced;
}

export function setHokmPublicAdvanced(value: boolean): void {
  hokmPublicAdvanced = value;
  try {
    localStorage.setItem(HOKM_PUBLIC_ADVANCED_KEY, value ? "1" : "0");
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

/**
 * «تأیید قبل از بازی کارت» — LIFT A CARD, THEN TAP THE TABLE TO PLAY IT (حکم + بی‌دل).
 *
 * Operator, 2026-10-08: players sometimes play a card by mistake. With this ON a tap on a card only
 * lifts it (a second tap puts it back, a tap on another playable card switches) and a tap on the
 * table plays the lifted card — `game/lift-to-play.ts`. OPT-IN, DEFAULT OFF: a player who never
 * opens تنظیمات plays exactly as before. Only the Settings screen sets it, and no table can open
 * that screen, so a table reads it when it renders and it never changes under a mounted table.
 */
let liftToPlay: boolean = readLiftToPlay();

function readLiftToPlay(): boolean {
  try {
    // Strictly the string "1": any other stored value means OFF.
    return localStorage.getItem(LIFT_TO_PLAY_KEY) === "1";
  } catch {
    return false;
  }
}

export function getLiftToPlay(): boolean {
  return liftToPlay;
}

export function setLiftToPlay(value: boolean): void {
  liftToPlay = value;
  try {
    localStorage.setItem(LIFT_TO_PLAY_KEY, value ? "1" : "0");
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}
